import { useCallback, useContext, useMemo } from 'react';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { UploadedFile } from '@src/types/filestack';

import { FilestackWrapperConstructor } from '../types/filestack';

declare global {
  interface Window {
    FilestackWrapper: FilestackWrapperConstructor;
  }
}

interface HandlerProps {
  onImageUploadSuccess: (imageEl: HTMLImageElement) => void;
  onImageUploadStarted?: () => void;
  onImageUploadFailed?: () => void;
  onImageUploadCancelled?: () => void;
}

const basePickerOptions = {
  maxFiles: 10,
  storeTo: {
    location: 's3',
    path: '/temporary_uploads/filestack/',
    access: 'public'
  },
  viewType: 'grid'
};

const transformOptions = {
  rotate: { deg: 'exif' }
};

export default function useFilestackUploader(
  fileLimit = 10
): (config: HandlerProps) => void {
  const { awsBucket, awsRegion } = useContext(WysiwygEditorStateContext);

  const pickerOptions = useMemo(() => {
    return {
      ...basePickerOptions,
      maxFiles: fileLimit,
      storeTo: {
        ...basePickerOptions.storeTo,
        container: awsBucket,
        region: awsRegion
      }
    };
  }, [awsBucket, awsRegion, fileLimit]);

  const handleUploadSuccess = useCallback(
    (file: UploadedFile, onSuccess: (imageEl: HTMLImageElement) => void) => {
      if (typeof window === 'undefined') return;

      const fsClient = new window.FilestackWrapper();
      const memoryImage = new window.Image();
      const rotatedUrl = fsClient.transform(file.handle, transformOptions);

      memoryImage.onload = function () {
        onSuccess(memoryImage);
      };

      memoryImage.onerror = function () {
        alert('There was a problem loading an image, please try again.');
      };

      memoryImage.src = rotatedUrl;
    },
    []
  );

  const handleUploadImage = useCallback(
    ({
      onImageUploadSuccess = () => undefined,
      onImageUploadStarted = () => undefined,
      onImageUploadFailed = () => undefined,
      onImageUploadCancelled = () => undefined
    }: HandlerProps) => {
      const fsClient = new window.FilestackWrapper();

      fsClient.filePicker(
        Object.assign({}, pickerOptions, {
          onUploadStarted: onImageUploadStarted,
          onCancel: onImageUploadCancelled,
          onFileUploadFailed: onImageUploadFailed
        }),
        function (file) {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          handleUploadSuccess(file, onImageUploadSuccess);
        },
        function (_error) {
          // no op, upload failed
        }
      );
    },
    [handleUploadSuccess, pickerOptions]
  );

  return handleUploadImage;
}
